import { Collapse, Flex, Progress, Tag, Typography } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PipelineStepState } from '../../types';
import {
  formatReceivedAt,
  previewOutput,
  resolveStreamProgressStatus,
} from './utils';

const { Text } = Typography;

type PipelineStreamFeedProps = {
  activeStep: PipelineStepState;
};

export function PipelineStreamFeed({ activeStep }: PipelineStreamFeedProps) {
  const { t } = useTranslation('pipelineDebug');
  const listRef = useRef<HTMLDivElement>(null);
  const items = activeStep.streamItems ?? [];
  const total = activeStep.streamTotal ?? 0;
  const isRunning = activeStep.status === 'running';
  const isSummary = activeStep.streamMode === 'summary';
  const showFeed = isRunning || items.length > 0;

  useEffect(() => {
    if (!listRef.current || items.length === 0) {
      return;
    }

    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [items.length, activeStep.streamProgress?.index]);

  if (!showFeed) {
    return null;
  }

  // A source log can fan out into several results, so progress counts source logs.
  const completedCount = new Set(items.map((item) => item.index)).size;
  const percent =
    total > 0 ? Math.round((completedCount / total) * 100) : isRunning ? 5 : 0;
  const currentIndex = activeStep.streamProgress?.index;
  const currentLogId = activeStep.streamProgress?.logId;

  return (
    <div className="pipeline-debug-stream-feed">
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Text strong>
            {isSummary ? t('stream.summaryTitle') : t('stream.title')}
          </Text>
          <Text type="secondary">
            {t(isSummary ? 'stream.summaryCounter' : 'stream.counter', {
              completed: completedCount,
              total: total || '…',
            })}
          </Text>
        </Flex>

        {isSummary ? (
          <Text type="secondary">{t('stream.summaryHint')}</Text>
        ) : null}

        <Progress
          percent={percent}
          status={resolveStreamProgressStatus(
            activeStep,
            isRunning,
            completedCount,
          )}
          showInfo={false}
          strokeColor="var(--step-accent, #1E6DF6)"
        />

        {isRunning && currentIndex !== undefined ? (
          <Text type="secondary" className="pipeline-debug-stream-current">
            {t('stream.processing', {
              index: currentIndex + 1,
              total: total || '…',
              logId: currentLogId ?? t('stream.unknownLog'),
            })}
          </Text>
        ) : null}

        <div ref={listRef} className="pipeline-debug-stream-list">
          {items.length === 0 ? (
            <Text type="secondary">
              {isSummary ? t('stream.summaryWaiting') : t('stream.waiting')}
            </Text>
          ) : (
            items.map((item) => (
              <div
                key={`${item.index}-${item.partIndex ?? 0}-${item.receivedAt}`}
                className={`pipeline-debug-stream-item${
                  item.error ? ' pipeline-debug-stream-item--error' : ''
                }`}
              >
                <Flex justify="space-between" align="center" wrap gap={8}>
                  <Flex gap={8} align="center" wrap>
                    <Text strong>
                      {item.partTotal
                        ? t('stream.itemPartLabel', {
                            index: item.index + 1,
                            total: item.total,
                            part: (item.partIndex ?? 0) + 1,
                            partTotal: item.partTotal,
                          })
                        : t('stream.itemLabel', {
                            index: item.index + 1,
                            total: item.total,
                          })}
                    </Text>
                    {item.logId ? (
                      <Text code className="pipeline-debug-stream-log-id">
                        {item.logId}
                      </Text>
                    ) : null}
                  </Flex>
                  <Flex gap={8} align="center">
                    {isSummary ? null : (
                      <Text
                        type="secondary"
                        className="pipeline-debug-stream-time"
                      >
                        {formatReceivedAt(item.receivedAt)}
                      </Text>
                    )}
                    <Tag
                      color={
                        item.error ? 'error' : isSummary ? 'default' : 'success'
                      }
                    >
                      {item.error
                        ? t('stream.failed')
                        : isSummary
                          ? t('stream.listed')
                          : t('stream.ok')}
                    </Tag>
                  </Flex>
                </Flex>

                <Text className="pipeline-debug-stream-preview">
                  {previewOutput(item.output, item.error)}
                </Text>

                <Collapse
                  bordered={false}
                  className="pipeline-debug-stream-collapse"
                  items={[
                    {
                      key: 'json',
                      label: t('stream.viewJson'),
                      children: (
                        <pre className="pipeline-debug-stream-json">
                          {JSON.stringify(
                            item.error
                              ? { error: item.error, output: item.output }
                              : item.output,
                            null,
                            2,
                          )}
                        </pre>
                      ),
                    },
                  ]}
                />
              </div>
            ))
          )}
        </div>
      </Flex>
    </div>
  );
}
